import type { LSGUpdateReportSettings } from '@brightlocal/api-client';
import { REPORT_SCHEDULE } from '@brightlocal/shared';

import {
  SCHEDULE_FORM_FIELDS,
  SCHEDULE_FREQUENCY,
  type OfferedFrequency,
  type ScheduleTimeSlot
} from 'shared/modules/report-schedule';
import type { TrackedTermsCopy, TrackedTermsDataHooks, TrackedTermsEventNames } from 'shared/modules/tracked-terms';

/** Data hook of the Schedule section; its card, drawer and buttons extend it. */
export const SCHEDULE_SECTION_DATA_HOOK = 'lsg-settings-schedule';

/** Data hook of the Keywords section; its card, drawer and buttons extend it. */
export const KEYWORDS_SECTION_DATA_HOOK = 'lsg-settings-keywords';

/** Most keywords an LSG report can track, and the limit when the plan sets none or doesn't load (the backend default too). */
export const LSG_MAX_KEYWORDS = 30;

/** `data-hook`s of the keywords editor: `lsg-settings-keyword-input`, `lsg-settings-keywords-table` and so on. */
export const KEYWORDS_LIST_DATA_HOOKS = {
  dataHook: 'lsg-settings',
  itemHook: 'keyword'
} as const satisfies TrackedTermsDataHooks;

const KEYWORDS_KEY_PREFIX = 'reports.localSearchGrid.settings.keywords';

/** i18n keys of the keywords editor. */
export const KEYWORDS_LIST_COPY = {
  inputLabel: `${KEYWORDS_KEY_PREFIX}.input.label`,
  inputPlaceholder: `${KEYWORDS_KEY_PREFIX}.input.placeholder`,
  add: `${KEYWORDS_KEY_PREFIX}.input.add`,
  duplicateError: `${KEYWORDS_KEY_PREFIX}.input.duplicateError`,
  counter: {
    added: `${KEYWORDS_KEY_PREFIX}.counter.added`,
    threeLeft: `${KEYWORDS_KEY_PREFIX}.counter.threeLeft`,
    twoLeft: `${KEYWORDS_KEY_PREFIX}.counter.twoLeft`,
    oneLeft: `${KEYWORDS_KEY_PREFIX}.counter.oneLeft`,
    full: `${KEYWORDS_KEY_PREFIX}.counter.full`,
    over: `${KEYWORDS_KEY_PREFIX}.counter.over`
  },
  column: `${KEYWORDS_KEY_PREFIX}.table.column`,
  empty: `${KEYWORDS_KEY_PREFIX}.table.empty`,
  selectAll: `${KEYWORDS_KEY_PREFIX}.table.selectAll`,
  selectRow: `${KEYWORDS_KEY_PREFIX}.table.selectRow`,
  deleteRow: `${KEYWORDS_KEY_PREFIX}.table.deleteRow`,
  selectedCount: `${KEYWORDS_KEY_PREFIX}.selection.selectedCount`,
  removeSelected: `${KEYWORDS_KEY_PREFIX}.selection.removeSelected`,
  deleteDialog: {
    title: `${KEYWORDS_KEY_PREFIX}.deleteDialog.title`,
    description: `${KEYWORDS_KEY_PREFIX}.deleteDialog.description`,
    cancel: `${KEYWORDS_KEY_PREFIX}.deleteDialog.cancel`,
    confirm: `${KEYWORDS_KEY_PREFIX}.deleteDialog.confirm`
  },
  saved: {
    itemAdded: `${KEYWORDS_KEY_PREFIX}.saved.keywordAdded`,
    itemDeleted: `${KEYWORDS_KEY_PREFIX}.saved.keywordDeleted`,
    updated: `${KEYWORDS_KEY_PREFIX}.saved.updated`,
    added: `${KEYWORDS_KEY_PREFIX}.saved.added`,
    deleted: `${KEYWORDS_KEY_PREFIX}.saved.deleted`
  }
} as const satisfies TrackedTermsCopy;

/** Data hook of the Email alerts section; its card, drawer and fields extend it. */
export const EMAIL_ALERTS_SECTION_DATA_HOOK = 'lsg-settings-email-alerts';

/** Data hook of the Map criteria card. */
export const MAP_CRITERIA_DATA_HOOK = 'lsg-settings-map-criteria';

/** Most addresses an LSG report emails when an update finishes; the API refuses more. */
export const LSG_MAX_EMAIL_ALERT_ADDRESSES = 5;

/** Fields of the Email alerts form. */
export const EMAIL_ALERTS_FORM_FIELDS = {
  IS_ENABLED: 'isEnabled',
  ADDRESSES: 'addresses'
} as const;

/** LSG update field of the Email alerts addresses; the api-client hands the error keys in camelCase. */
export const LSG_EMAIL_ALERT_ADDRESSES_ERROR_FIELD = 'emailAlertAddresses' satisfies keyof LSGUpdateReportSettings;

/** Why a typed address isn't added to the Email alerts list. */
export const EMAIL_ALERT_ADDRESS_ISSUE = {
  LIMIT: 'limit',
  DUPLICATE: 'duplicate'
} as const;

/** Message under Send to for each reason an address isn't added; the duplicate one names the address. */
export const EMAIL_ALERT_ADDRESS_ISSUE_KEYS = {
  [EMAIL_ALERT_ADDRESS_ISSUE.LIMIT]: 'reports.localSearchGrid.settings.emailAlerts.addresses.limit',
  [EMAIL_ALERT_ADDRESS_ISSUE.DUPLICATE]: 'reports.localSearchGrid.settings.emailAlerts.addresses.duplicate'
} as const satisfies Record<(typeof EMAIL_ALERT_ADDRESS_ISSUE)[keyof typeof EMAIL_ALERT_ADDRESS_ISSUE], string>;

/**
 * The grid spacing the API sends: metres under 1 km (`500m`), otherwise km or miles to one decimal
 * (`2.5km`, `0.5mi`). The contract lists 19 such values; a spacing outside the pattern shows as sent.
 */
export const LSG_GRID_POINT_SPACING_PATTERN = /^(?<value>\d+(?:\.\d+)?)(?<unit>km|mi|m)$/;

/** LSG `run_time` label of each Time of day window: the API sends and accepts the label, not the time. */
export const LSG_RUN_TIMES = {
  '00:00:00': '12:00 AM - 03:00 AM',
  '03:00:00': '03:00 AM - 06:00 AM',
  '06:00:00': '06:00 AM - 09:00 AM',
  '09:00:00': '09:00 AM - 12:00 PM',
  '12:00:00': '12:00 PM - 03:00 PM',
  '15:00:00': '03:00 PM - 06:00 PM',
  '18:00:00': '06:00 PM - 09:00 PM',
  '21:00:00': '09:00 PM - 12:00 AM'
} as const satisfies Record<ScheduleTimeSlot, string>;

/** LSG `schedule` each How often option saves as. */
export const LSG_SCHEDULES = {
  [SCHEDULE_FREQUENCY.MANUAL]: REPORT_SCHEDULE.ADHOC,
  [SCHEDULE_FREQUENCY.WEEKLY]: REPORT_SCHEDULE.WEEKLY,
  [SCHEDULE_FREQUENCY.MONTHLY]: REPORT_SCHEDULE.MONTHLY
} as const satisfies Record<OfferedFrequency, NonNullable<LSGUpdateReportSettings['schedule']>>;

/** GTM `eventAction` of the settings page events: the section the customer edits or saves. */
export const LSG_SETTINGS_SECTION = {
  SCHEDULE: 'schedule',
  KEYWORDS: 'keywords',
  EMAIL_ALERTS: 'email_alerts'
} as const;

/** One of the {@link LSG_SETTINGS_SECTION} values. */
export type LsgSettingsSection = (typeof LSG_SETTINGS_SECTION)[keyof typeof LSG_SETTINGS_SECTION];

/** GTM `eventAction` of `old_platform_link_clicked`: the card the link is on. */
export const LSG_OLD_PLATFORM_LINK = {
  MAP_CRITERIA: 'map_criteria'
} as const;

/** GTM event names of the keywords editor. */
export const LSG_KEYWORD_EVENT_NAMES = {
  added: 'keyword_added',
  duplicateError: 'keyword_added_error',
  removed: 'keyword_removed',
  removalCancelled: 'keyword_removal_cancelled'
} as const satisfies TrackedTermsEventNames;

/** GTM `eventLabel` of `email_alerts_saved`: whether alerts are on after the save. */
export const LSG_EMAIL_ALERTS_STATE = {
  ON: 'on',
  OFF: 'off'
} as const;

/**
 * Frozen analytics text of `settings_save_failed`, one per kind of failure, independent of the UI copy,
 * which may change without breaking the GA data. Each fits the 40-character description with its
 * `server - ` prefix.
 */
export const LSG_SAVE_FAILED_MESSAGES = {
  NETWORK: 'connection failed',
  KEYWORDS_REFUSED: 'keywords refused',
  SCHEDULE_REFUSED: 'schedule refused',
  EMAIL_ADDRESSES_REFUSED: 'email addresses refused',
  OTHER: "couldn't save changes"
} as const;

/**
 * Frozen analytics value of the duplicate-keyword error, independent of the UI copy
 * (`KEYWORDS_LIST_COPY.duplicateError`), which may change without breaking the GA data.
 */
export const LSG_DUPLICATE_KEYWORD_ERROR_MESSAGE = "you've already added this keyword";

/** Schedule form field each LSG update error belongs to; the api-client hands the error keys in camelCase. */
export const LSG_SCHEDULE_ERROR_FIELDS = {
  schedule: SCHEDULE_FORM_FIELDS.FREQUENCY,
  dayOfWeek: SCHEDULE_FORM_FIELDS.RUN_ON,
  dayOfMonth: SCHEDULE_FORM_FIELDS.RUN_ON,
  runTime: SCHEDULE_FORM_FIELDS.TIME_SLOT
} as const satisfies Partial<
  Record<keyof LSGUpdateReportSettings, (typeof SCHEDULE_FORM_FIELDS)[keyof typeof SCHEDULE_FORM_FIELDS]>
>;
